// Scalar-value slot recognizer — value-slot plan T5a (D2). SYNTAX-ONLY: fires
// when the cursor sits at a clean value operand after a binary /
// [NOT ]LIKE/ILIKE comparison operator whose LHS operand is COMPLETE
// (identifier, dotted ref, `)`-closed call, numeric literal) — incl. chained
// predicates. Clause/engine legality of the result is intellisense-owned (the
// T5b capability gate); this layer does not consult the clause.
//
// Explicitly NOT recognized here:
//   - `IN ( … )` elements — `IN (` opens a `'subquery'` clause frame (G12);
//   - a window-frame-extent BETWEEN (`ROWS|RANGE|GROUPS BETWEEN ▮` — T16
//     guard: a bare, unquoted LHS of exactly ROWS/RANGE/GROUPS is a frame
//     unit, not a column, so the low-bound match is rejected; multi-word
//     frame bounds (`UNBOUNDED PRECEDING`, `1 PRECEDING`) never satisfy the
//     high-bound's "exactly one simple token" rule in the first place — see
//     `editorContextSlot.betweenScan.ts`'s header);
//   - function-argument / tuple positions (`fn(▮`, `(a, ▮`) — cursor preceded
//     by `(` or `,` is rejected.
//
// Recognizer/extractor split (D2/F2): `recognizeValueSlot` fires even when the
// LHS is a non-column expression (`UPPER(name) = ▮`); `extractComparisonLhs`
// is best-effort and may return undefined — `lhsRange` is always present.
//
// Pure text scan (G11-rule): no tree-sitter, no intellisense imports. The OLD
// detector (`intellisense/context/detectComparisonValueContext.ts`) stays —
// the S0 guard and the enum collectors keep using it (G8).

import type { ValueSlotInfo } from '../model/editorContext';
import { matchBetweenBound } from './editorContextSlot.betweenScan';
import { scanInListHead } from './editorContextSlot.inScan';
import { scanSetAssignmentContext } from './editorContextSlot.setScan';
import {
  matchComparisonOperator,
  scanLhsOperand,
  scanValueTextState,
} from './editorContextSlot.valueScan';

// T16 window-frame guard: `ROWS|RANGE|GROUPS BETWEEN ▮` (frame-start extent)
// scans identically to `<column> BETWEEN ▮` — the frame-unit keyword is a
// bare, unquoted, single-part LHS just like a real column would be. Reject
// it outright rather than misclassify a window-frame keyword-only slot as a
// scalar-value comparison (would leak real columns into a position that
// must stay keyword-only — see pgsql/mysql/oracle/sqlite resolver
// `isXxxFrameStartPosition` + their "columns must not bleed in" pins).
// Accepted gap (mirrors T15's SET-keyword-shadow note): a column literally
// named `rows`/`range`/`groups` used bare in a real BETWEEN is a reserved
// word in this position for every supported engine, so this stays a
// theoretical corner.
const FRAME_UNIT_KEYWORDS = new Set(['ROWS', 'RANGE', 'GROUPS']);

/** The pattern-match operators the dialects add on top of LIKE/ILIKE. Only
 *  these are subject to the clause-keyword LHS guard below — see the comment
 *  at its site for why LIKE/ILIKE are deliberately excluded. */
const DIALECT_WORD_OPERATORS: ReadonlySet<string> = new Set([
  'GLOB', 'MATCH', 'REGEXP', 'RLIKE',
]);

/** Keywords that can sit immediately before the START of a predicate. A word
 *  operator reading one of these as its LHS means the "operator" actually
 *  LEADS the expression and is not infix. */
const PREDICATE_LEAD_KEYWORDS: ReadonlySet<string> = new Set([
  'WHERE', 'AND', 'OR', 'NOT', 'ON', 'HAVING', 'WHEN', 'THEN', 'ELSE',
  'SELECT', 'SET', 'BY',
]);

export function recognizeValueSlot(args: {
  readonly source: string;
  readonly stmtStart: number;
  readonly offset: number;
  readonly engine: string;
}): ValueSlotInfo | undefined {
  const { source, stmtStart, offset, engine } = args;
  if (offset < stmtStart || offset > source.length) return undefined;

  const state = scanValueTextState(source, stmtStart, offset, engine);
  if (state.kind === 'opaque') return undefined;

  // Boundary = exclusive end of the char run that must form the operator.
  let boundaryEnd: number;
  if (state.kind === 'sq-string') {
    // The cursor's operand is the enclosing string; the operator must sit
    // directly left of its opening quote (optionally behind a T-SQL N prefix).
    let b = state.openQuote;
    if (
      b - 1 >= stmtStart &&
      (source[b - 1] === 'N' || source[b - 1] === 'n') &&
      (b - 1 === stmtStart || !/[A-Za-z0-9_$]/.test(source[b - 2]!))
    ) {
      b -= 1;
    }
    boundaryEnd = b;
  } else {
    // Code state: validate the cursor's IMMEDIATE operand — empty (`= ▮`) or
    // a bare partial token attached to the cursor (`= pen▮`). Anything else
    // (`,` / `(` / digit / `)`) is not a stage-A value slot. Newlines count
    // as whitespace (C3 sol finding 4: `WHERE amount =\n  ▮` is normal
    // formatting and must open the slot like its single-line form).
    let j = offset - 1;
    let sawWs = false;
    while (j >= stmtStart && /\s/.test(source[j]!)) {
      j -= 1;
      sawWs = true;
    }
    if (j < stmtStart) return undefined;
    const ch = source[j]!;
    if (!sawWs && /[A-Za-z0-9_$]/.test(ch)) {
      // Attached bare partial → skip the whole run; a run STARTING with a
      // digit is a numeric literal (a typed value, not an identifier prefix)
      // and does not re-open the slot (C1 SHOULD-FIX 1: the run may END in
      // `_` / digit / `$` — `= order_▮`, `= pen1▮` must still fire).
      let k = j;
      while (k >= stmtStart && /[A-Za-z0-9_$]/.test(source[k]!)) k -= 1;
      if (/[0-9]/.test(source[k + 1]!)) return undefined;
      boundaryEnd = k + 1;
    } else if (sawWs && /[A-Za-z]/.test(ch)) {
      // A ws-separated word is the boundary itself (the word operator —
      // consuming it broke `x LIKE ▮` / `x GLOB ▮`).
      boundaryEnd = j + 1;
    } else if (ch === '=' || ch === '<' || ch === '>') {
      boundaryEnd = j + 1;
    } else if (ch === '(' || ch === ',') {
      // T17: a `(`/`,` boundary is not a stage-A comparison slot, but it IS
      // the element-delimiter shape of an IN list — let it flow through to
      // the IN arm below (the operator/BETWEEN matchers fail on it
      // naturally; non-IN groups are rejected by `scanInListHead`).
      boundaryEnd = j + 1;
    } else {
      return undefined;
    }
  }

  const op = matchComparisonOperator(source, boundaryEnd, stmtStart, engine);
  if (op === undefined) {
    // T16 — BETWEEN bounds. Tried after the symbolic/word operator match
    // fails: `matchComparisonOperator`'s own word-branch recognizes only the
    // pattern-match family (LIKE/ILIKE plus the engine's own GLOB / MATCH /
    // REGEXP / RLIKE), so
    // BETWEEN/AND never satisfy it and `op` is always `undefined` on a
    // genuine BETWEEN shape.
    const between = matchBetweenBound(source, boundaryEnd, stmtStart);
    if (between === undefined) return recognizeInListItem(source, stmtStart, boundaryEnd, engine);
    const lhs = scanLhsOperand(source, between.opStart, stmtStart);
    if (lhs === undefined) return undefined;
    if (
      lhs.shape === 'ref' &&
      FRAME_UNIT_KEYWORDS.has(source.slice(lhs.start, lhs.end).toUpperCase())
    ) {
      return undefined;
    }
    return {
      role: between.bound === 'low' ? 'between-low' : 'between-high',
      negated: between.negated,
      lhs: extractComparisonLhs(source, lhs),
      lhsRange: { start: lhs.start, end: lhs.end },
    };
  }
  const lhs = scanLhsOperand(source, op.opStart, stmtStart);
  if (lhs === undefined) return undefined;
  // A dialect pattern-match operator whose LHS scans to a bare clause keyword
  // is not an infix comparison at all: `WHERE MATCH ▮` is mysql's full-text
  // `MATCH (cols) AGAINST (…)` head, where MATCH LEADS the predicate. The LHS
  // scanner accepts any identifier-shaped token, so the keyword has to be
  // rejected here — same shape as the FRAME_UNIT_KEYWORDS guard above. Scoped
  // to the dialect operators: LIKE/ILIKE keep their pinned behavior, and a
  // column named `row` / `end` / `start` (all in the broad SQL keyword set)
  // stays a legal LHS for every operator.
  if (
    DIALECT_WORD_OPERATORS.has(op.operator) &&
    lhs.shape === 'ref' &&
    PREDICATE_LEAD_KEYWORDS.has(source.slice(lhs.start, lhs.end).toUpperCase())
  ) {
    return undefined;
  }
  // T15: a bare `=` may be a DML SET assignment rather than a comparison —
  // role-split via the statement-head scan. Non-DML SET statements (session /
  // procedural) refuse the slot outright.
  if (op.operator === '=' && !op.negated) {
    const setScan = scanSetAssignmentContext(source, stmtStart, lhs.start, engine);
    if (setScan === 'non-dml-set') return undefined;
    if (setScan === 'set-assignment') {
      return {
        role: 'set-rhs',
        lhs: extractComparisonLhs(source, lhs),
        lhsRange: { start: lhs.start, end: lhs.end },
      };
    }
  }
  return {
    role: 'comparison-rhs',
    operator: op.operator,
    negated: op.negated,
    lhs: extractComparisonLhs(source, lhs),
    lhsRange: { start: lhs.start, end: lhs.end },
  };
}

/** T17 — the in-list-item arm, tried when neither a comparison operator nor
 *  a BETWEEN bound matches at `boundaryEnd`. Fires only when (a) the cursor's
 *  operand attaches to an element delimiter (`(` or `,` directly left of the
 *  boundary, ws-tolerant), (b) the enclosing group is a scalar `[NOT] IN (`
 *  with no `SELECT` interior (`scanInListHead`), and (c) the IN LHS is a
 *  single-column operand — a bare tuple head `(a, b) IN (` is refused
 *  (F23: multi-column IN stays clause-classified, gaps-doc'd). */
function recognizeInListItem(
  source: string,
  stmtStart: number,
  boundaryEnd: number,
  engine: string,
): ValueSlotInfo | undefined {
  let p = boundaryEnd;
  while (p > stmtStart && /\s/.test(source[p - 1]!)) p -= 1;
  const delim = p > stmtStart ? source[p - 1] : '';
  if (delim !== '(' && delim !== ',') return undefined;
  const head = scanInListHead(source, stmtStart, boundaryEnd, engine);
  if (head === undefined) return undefined;
  const lhs = scanLhsOperand(source, head.opStart, stmtStart);
  if (lhs === undefined) return undefined;
  if (lhs.shape === 'call' && !isSingleColumnCallLhs(source, lhs)) return undefined; // tuple (F23)
  return {
    role: 'in-list-item',
    negated: head.negated,
    lhs: extractComparisonLhs(source, lhs),
    lhsRange: { start: lhs.start, end: lhs.end },
  };
}

/** F23 tuple-vs-call disambiguation for a `)-closed` IN LHS: a genuine call
 *  has a non-keyword callee identifier attached to its `(`; a bare tuple
 *  either starts at `(` outright or made `scanLhsOperand`'s callee walk pick
 *  up the PRECEDING clause keyword (`WHERE (a, b) IN (` reads `WHERE` as the
 *  "callee"). */
const NON_CALLEE_KEYWORDS = new Set([
  'where', 'and', 'or', 'on', 'having', 'when', 'then', 'else', 'select',
  'from', 'by', 'in', 'not', 'set', 'values', 'join', 'case', 'exists',
]);

function isSingleColumnCallLhs(
  source: string,
  lhs: { readonly start: number; readonly end: number },
): boolean {
  const openIdx = source.indexOf('(', lhs.start);
  if (openIdx === -1 || openIdx >= lhs.end) return false;
  const callee = source.slice(lhs.start, openIdx).trim();
  if (callee === '') return false;
  return !NON_CALLEE_KEYWORDS.has(callee.toLowerCase());
}

/** Best-effort DottedBase-shaped LHS parse over `range` — quoting preserved
 *  (`quoted[i]` true when part i was `"…"` / `` `…` `` / `[…]`-delimited;
 *  parts hold the inner spelling verbatim). Undefined for anything that is
 *  not a plain (dotted) column ref — calls, numbers, expressions (F2). */
export function extractComparisonLhs(
  source: string,
  range: { readonly start: number; readonly end: number },
): { readonly parts: readonly string[]; readonly quoted: readonly boolean[] } | undefined {
  const parts: string[] = [];
  const quoted: boolean[] = [];
  let i = range.start;
  const end = range.end;
  while (i < end) {
    while (i < end && /\s/.test(source[i]!)) i += 1;
    const ch = source[i];
    if (ch === '"' || ch === '`') {
      const close = source.indexOf(ch, i + 1);
      if (close === -1 || close >= end) return undefined;
      parts.push(source.slice(i + 1, close));
      quoted.push(true);
      i = close + 1;
    } else if (ch === '[') {
      const close = source.indexOf(']', i + 1);
      if (close === -1 || close >= end) return undefined;
      parts.push(source.slice(i + 1, close));
      quoted.push(true);
      i = close + 1;
    } else if (ch !== undefined && /[A-Za-z_$]/.test(ch)) {
      let j = i + 1;
      while (j < end && /[A-Za-z0-9_$]/.test(source[j]!)) j += 1;
      parts.push(source.slice(i, j));
      quoted.push(false);
      i = j;
    } else {
      return undefined;
    }
    while (i < end && /\s/.test(source[i]!)) i += 1;
    if (i >= end) break;
    if (source[i] !== '.') return undefined;
    i += 1;
  }
  return parts.length > 0 ? { parts, quoted } : undefined;
}
